import { getCoverDisplayUrl } from '../utils/coverUrl';
import React from 'react';
import { Button } from './ui/button';
import { Play, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Use current origin for deployed environment
const getAssetBaseUrl = () => window.location.origin;

const Hero = ({ book }) => {
  const navigate = useNavigate();

  if (!book) return null;

  // Build cover URL - always use current origin for production compatibility
  const getCoverUrl = (url) => {
    if (!url) return null;
    // If it's already an absolute URL, use as-is
    if (url.startsWith('http')) return url;
    // For relative URLs starting with /api, use current origin
    if (url.startsWith('/api')) {
      return `${getAssetBaseUrl()}${url}`;
    }
    return url;
  };

  const coverUrl = getCoverDisplayUrl(book.cover_image_url || book.coverImage);

  return (
    <div className="relative w-full min-h-[60vh] sm:min-h-[65vh] md:min-h-[70vh] overflow-hidden" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src={coverUrl}
          alt={book.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20 flex items-center">
        <div className="max-w-2xl space-y-4 sm:space-y-5 md:space-y-6">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-red-600/90 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded">
            <span className="text-white font-bold text-xs sm:text-sm tracking-wider">FEATURED BOOK</span>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight" data-testid="hero-title">
            {book.title}
          </h1>

          {/* Author */}
          <p className="text-lg sm:text-xl md:text-2xl text-gray-300 font-medium">
            by {book.author}
          </p>

          {/* Info - responsive flex wrap */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-300">
            <span className="flex items-center space-x-1">
              <span className="text-yellow-400">★</span>
              <span>{book.rating || 0}</span>
            </span>
            <span className="hidden sm:inline">•</span>
            <span>{book.publish_year || book.publishYear}</span>
            <span className="hidden sm:inline">•</span>
            <span>{book.pages} pages</span>
            {book.genres && book.genres[0] && (
              <>
                <span className="hidden sm:inline">•</span>
                <span className="px-2 py-1 bg-white/10 rounded text-xs sm:text-sm">{book.genres[0]}</span>
              </>
            )}
          </div>

          {/* Description - hide on very small screens */}
          <p className="hidden sm:block text-base md:text-lg text-white/90 leading-relaxed max-w-xl line-clamp-3">
            {book.description}
          </p>

          {/* Buttons - stack on mobile */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 pt-2 sm:pt-4">
            <Button
              size="lg"
              onClick={() => navigate(`/book/${book.id}`)}
              className="bg-white text-black hover:bg-white/90 font-semibold px-6 py-5 sm:px-8 sm:py-6 text-base sm:text-lg transition-transform hover:scale-105 w-full sm:w-auto"
              data-testid="hero-read-preview-btn"
            >
              <Play className="mr-2 h-5 w-5 sm:h-6 sm:w-6 fill-current" />
              Read Preview
            </Button>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => navigate(`/book/${book.id}`)}
              className="bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 font-semibold px-6 py-5 sm:px-8 sm:py-6 text-base sm:text-lg border border-white/30 transition-transform hover:scale-105 w-full sm:w-auto"
              data-testid="hero-more-info-btn"
            >
              <Info className="mr-2 h-5 w-5 sm:h-6 sm:w-6" />
              More Info
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;